import { useNewCode } from '@calendly/feature-check-react';
import { useQueryParams } from '@calendly/hooks';
import { I18n } from '@calendly/i18n';
import { IconSettings } from '@calendly/ui/components/glyphs/IconSettings';
import { IconButton } from '@calendly/ui/components/icon-button';
import { Loader } from '@calendly/ui/components/loader';
import { PageHeader } from '@calendly/ui/components/page-header';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { isEmpty } from 'lodash-es';
import { useEffect, useRef, useMemo, useCallback, forwardRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink, Route, Switch, useRouteMatch } from 'react-router-dom';
import { useGetUserQuery } from 'common';
import { ANALYTICS_CTA_CLICK_PROPS_MAP, EMPTY_STATE_TYPES, EmptyState } from 'components/empty_state';
import { useSprig } from 'components/sprig';
import { useProductRefreshAppShell } from 'features/app_shell/product_refresh/useProductRefreshAppShell';
import { BILLING_ENTRYPOINTS, isAvailableFeatureSelector } from 'features/billing';
import { CONTENT_PAGES } from 'features/empty_state';
import { useGetMeetingIntelligenceUserSettingsQuery, useNotetakerEntitled, useNotetakerFreeMeetingsQuota, FreeRecapsHeaderAction } from 'features/meeting_intelligence';
import { SETTINGS_SECTION } from 'features/settings_modal/constants';
import { SETTINGS_MODAL_AI_TAB } from 'features/settings_modal/settings_modal_ai_page';
import { useSettingsNavigation } from 'features/settings_modal/use_settings_navigation';
import { DismissableNotetakerBanner } from 'features/upgrade_banner';
import { useTrack, TRACKING_EVENT_NAMES, useTrackPageExit } from 'hooks/analytics_events';
import { useHomeTabs } from 'hooks/home/tabs';
import { useFilters } from 'hooks/meetings/filters';
import { useScope } from 'hooks/meetings/use_scope';
import { useNotetakerFiveFreeRecapsExperiment } from 'hooks/useNotetakerFiveFreeRecapsExperiment';
import { useNotetakerFreeExperiment } from 'hooks/useNotetakerFreeExperiment';
import { timezoneSelector } from 'selectors/current_user';
import { NOTETAKER_SETTINGS_PATH } from 'shared/constants/personal_browser_urls';
import { AllPagesAuthorization, AllUsersAuthorization, PageAuthorization, UserAuthorization, UserGroupAuthorization } from './authorization';
import { BulkRecapsActions } from './bulk_recaps_actions';
import { ButtonContainer } from './button_container';
import { RECAP_SHARING_ACCESS_FLAG } from './constants';
import { ErrorList } from './error_states';
import { EmptyFilteredList } from './error_states/empty_filtered_list';
import { EmptySharedList } from './error_states/empty_shared_list';
import { useMeetingsData } from './hooks/use_meetings_data';
import { useSelectedView } from './hooks/use_selected_view';
import { MeetingList } from './meeting_list';
import { MeetingCard } from './meeting_list/meeting_card';
import { MEETINGS_RECAP_SCOPE } from './recap_scope_tabs';
import { SearchAndFilter } from './search_and_filter';
import { isFilterSet, sampleMeeting } from './utils';
const styledEmptyContainerIsPhoneCss = "s17i9s1p";
const styledEmptyContainerCss = "snq44jy";
const StyledEmptyContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(styledEmptyContainerCss, {
    [styledEmptyContainerIsPhoneCss]: isPhone
  }, props.className)} />;
});
const analytics = Component => props => {
  const track = useTrack();
  const {
    sentAnalytics,
    contentKey
  } = props;
  const properties = ANALYTICS_CTA_CLICK_PROPS_MAP[contentKey];
  useEffect(() => {
    if (sentAnalytics.current) return;
    track({
      name: TRACKING_EVENT_NAMES.CTA_SHOWN,
      entities: ['attribution', 'organization', 'user'],
      properties
    });
    sentAnalytics.current = true;
  }, [track, properties, sentAnalytics]);
  return <Component {...props} />;
};
const getButtonText = ({
  isMeetingIntelligenceAvailable,
  hasRemainingFreeMeetings
}) => {
  if (hasRemainingFreeMeetings) return I18n.t('publisher.billing.meeting_intelligence.get_notetaker');
  if (isMeetingIntelligenceAvailable) return I18n.t('publisher.billing.meeting_intelligence.try_notetaker');
  return I18n.t('publisher.billing.meeting_intelligence.upgrade_plan');
};
const RecapsButtonContainer = () => {
  const isMeetingIntelligenceAvailable = useSelector(state => isAvailableFeatureSelector(state, {
    featureKey: 'meeting_intelligence'
  }));
  const {
    isInExperiment: isInNotetakerFreeExperiment
  } = useNotetakerFreeExperiment();
  const {
    isInExperiment: isInNotetakerFiveFreeRecapsExperiment
  } = useNotetakerFiveFreeRecapsExperiment();
  const isInMeteredNotetakerExperiment = isInNotetakerFreeExperiment || isInNotetakerFiveFreeRecapsExperiment;
  const {
    hasRemainingFreeMeetings
  } = useNotetakerFreeMeetingsQuota({
    skip: !isInMeteredNotetakerExperiment
  });
  return <ButtonContainer contentKey={CONTENT_PAGES.recaps} text={getButtonText({
    isMeetingIntelligenceAvailable,
    hasRemainingFreeMeetings
  })} />;
};
const InnerContent = ({
  data: meetings,
  hasPrevious,
  headerRef,
  isError,
  isLoading,
  isPreviousLoading,
  isSuccess,
  retrievePreviousPage,
  selectedView,
  selectedRecaps,
  setSelectedRecaps,
  deletedRecapUuids = [],
  isFeatureAvailable,
  hasRecapSharingAccess,
  recapScope,
  onDeletedRecap
}) => {
  const {
    getTabPathByName
  } = useHomeTabs();
  const {
    getFilterParams
  } = useFilters();
  const filterParams = getFilterParams({
    period: 'past'
  });
  const sentAnalytics = useRef(false);
  const {
    isLoading: isMeetingIntelligenceUserLoading,
    data: meetingIntelligenceUserData
  } = useGetMeetingIntelligenceUserSettingsQuery();
  const hasMIEnabled = !!meetingIntelligenceUserData?.miEnabled;
  const {
    isInExperiment: isInNotetakerFreeExperiment
  } = useNotetakerFreeExperiment();
  const {
    isInExperiment: isInNotetakerFiveFreeRecapsExperiment
  } = useNotetakerFiveFreeRecapsExperiment();
  const isInMeteredNotetakerExperiment = isInNotetakerFreeExperiment || isInNotetakerFiveFreeRecapsExperiment;
  const {
    hasRemainingFreeMeetings
  } = useNotetakerFreeMeetingsQuota({
    skip: !isInMeteredNotetakerExperiment
  });
  const shouldShowPaywallButton = hasMIEnabled && !isFeatureAvailable && isInMeteredNotetakerExperiment && !hasRemainingFreeMeetings;
  const timezone = useSelector(timezoneSelector);
  const {
    deletedRecapId
  } = useQueryParams();
  const isSharedWithMe = hasRecapSharingAccess && recapScope === MEETINGS_RECAP_SCOPE.SHARED_WITH_ME_ONLY;

  // Handle single delete from recap page or bulk delete from recaps page
  const filteredMeetings = useMemo(() => {
    if (!meetings) return meetings;
    let filtered = meetings;
    if (deletedRecapId) {
      filtered = filtered.filter(meeting => meeting.meetingIntelligence?.uuid !== deletedRecapId);
    } else if (deletedRecapUuids.length > 0) {
      filtered = filtered.filter(meeting => !deletedRecapUuids.includes(meeting.meetingIntelligence?.uuid));
    }
    return filtered;
  }, [meetings, deletedRecapId, deletedRecapUuids]);
  if (isLoading || isMeetingIntelligenceUserLoading) {
    return <Loader data-testid="meeting-list-loader" />;
  }
  if (!isSuccess || !meetings) return null;
  if (isEmpty(filteredMeetings) && isFilterSet(filterParams) && hasMIEnabled) return <EmptyFilteredList />;
  return <>
      {isError && <ErrorList />}
      {isEmpty(filteredMeetings) && isSharedWithMe && <EmptySharedList />}
      {isEmpty(filteredMeetings) && !isSharedWithMe && <StyledEmptyContainer>
          {!hasMIEnabled && <MeetingCard selectedView="grid" event={sampleMeeting(timezone)} isSample />}
          <EmptyState type={EMPTY_STATE_TYPES.leftRight} contentKey={!hasMIEnabled ? CONTENT_PAGES.recaps : CONTENT_PAGES.recapsMIEnabled} options={{
        url: getTabPathByName('event_types')
      }} buttonOverride={!hasMIEnabled || shouldShowPaywallButton ? RecapsButtonContainer : null} withAnalytics={analytics} sentAnalytics={sentAnalytics} />
        </StyledEmptyContainer>}
      {!isEmpty(filteredMeetings) && !isError && <MeetingList selectedView={selectedView} meetings={filteredMeetings} retrievePreviousPage={retrievePreviousPage} hasPrevious={hasPrevious} isPreviousLoading={isPreviousLoading} headerRef={headerRef} selectedRecaps={selectedRecaps} setSelectedRecaps={setSelectedRecaps} onDeletedRecap={onDeletedRecap} hasRecapSharingAccess={hasRecapSharingAccess} />}
    </>;
};
const RecapsPageContent = () => {
  const {
    scopeType,
    scopeId
  } = useScope();
  const selectedView = useSelectedView();
  const headerRef = useRef(null);
  const {
    getFilterParams
  } = useFilters();
  const filterParams = getFilterParams({
    period: 'past'
  });
  const sprig = useSprig();
  const {
    data: {
      id: userId
    } = {}
  } = useGetUserQuery();
  const hasTriggeredSurvey = useRef(false);
  const hasRecapSharingAccess = useNewCode(RECAP_SHARING_ACCESS_FLAG)?.enabled;
  const [recapScope, setRecapScope] = useState(MEETINGS_RECAP_SCOPE.ALL_RECAPS);
  const featureKey = 'meeting_intelligence';
  const isFeatureAvailable = useSelector(state => isAvailableFeatureSelector(state, {
    featureKey
  }));
  const {
    isInExperiment: isInNotetakerFreeExperiment
  } = useNotetakerFreeExperiment();
  const meetingsData = useMeetingsData({
    recapScope: hasRecapSharingAccess ? recapScope : MEETINGS_RECAP_SCOPE.ALL_RECAPS
  });
  const [selectedRecaps, setSelectedRecaps] = useState([]);
  const [deletedRecaps, setDeletedRecaps] = useState([]);
  const isProductRefresh = useProductRefreshAppShell();
  const {
    isEntitled: isNotetakerEntitled
  } = useNotetakerEntitled();
  const {
    hrefForSettingsModalEntry
  } = useSettingsNavigation();
  useEffect(() => {
    setSelectedRecaps([]);
  }, [recapScope]);
  const handleDeletedRecapsChange = useCallback(newDeletedRecaps => {
    setDeletedRecaps(prev => [...new Set([...prev, ...newDeletedRecaps])]);
  }, []);
  const handleDeletedRecap = useCallback(deletedRecapUuid => {
    setDeletedRecaps(prev => prev.includes(deletedRecapUuid) ? prev : [...prev, deletedRecapUuid]);
    setSelectedRecaps(prev => prev.filter(recapUuid => recapUuid !== deletedRecapUuid));
  }, []);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!hasTriggeredSurvey.current && !isEmpty(meetingsData.data)) {
        sprig.identifyAndTrack({
          eventName: 'mi.recap.page_popup',
          userId
        });
      }
      hasTriggeredSurvey.current = true;
    }, 7000);
    return () => clearTimeout(timer);
  }, [userId, sprig, hasTriggeredSurvey, meetingsData.data]);
  useTrackPageExit({
    routePattern: '/app/recaps',
    relatedRoutes: ['/app/tracked_meetings'],
    properties: {
      title: 'Recaps'
    }
  });
  return <>
      <PageHeader title={I18n.t(isProductRefresh ? 'publisher.recaps.navigation.header_rebrand' : 'publisher.recaps.navigation.header')} actions={[<FreeRecapsHeaderAction key="free-recaps" paywallLocation={BILLING_ENTRYPOINTS.RECAPS_PAGE} />, isNotetakerEntitled && <IconButton key="notetaker-settings" as={RouterLink} to={hrefForSettingsModalEntry(SETTINGS_SECTION.ai_notetaker, NOTETAKER_SETTINGS_PATH, SETTINGS_MODAL_AI_TAB.NOTETAKER)} icon={IconSettings} title={I18n.t('publisher.meetings.settings')} />].filter(Boolean)} />
      {(!isFeatureAvailable || isInNotetakerFreeExperiment) && <DismissableNotetakerBanner bannerType="recaps" featureKey={featureKey} isInNotetakerFreeExperiment={isInNotetakerFreeExperiment} />}
      <SearchAndFilter hideFilter={!hasRecapSharingAccess && !isFilterSet(filterParams) && isEmpty(meetingsData.data)} selectedView={selectedView} scope={{
      id: scopeId,
      type: scopeType
    }} ref={headerRef} recapScope={recapScope} setRecapScope={setRecapScope} hasRecapSharingAccess={hasRecapSharingAccess} />
      <InnerContent headerRef={headerRef} isFeatureAvailable={isFeatureAvailable} selectedView={selectedView} selectedRecaps={selectedRecaps} setSelectedRecaps={setSelectedRecaps} deletedRecapUuids={deletedRecaps} hasRecapSharingAccess={hasRecapSharingAccess} recapScope={recapScope} onDeletedRecap={handleDeletedRecap} {...meetingsData} />
      <BulkRecapsActions selectedRecaps={selectedRecaps} setSelectedRecaps={setSelectedRecaps} onDeletedRecapsChange={handleDeletedRecapsChange} />
    </>;
};
export const RecapsPage = () => {
  const {
    params
  } = useRouteMatch();
  const basePath = params[0];
  return <Switch>
      <Route exact path={`${basePath}/user/me`} component={RecapsPageContent} />
      <Route exact path={`${basePath}/team`}>
        <AllPagesAuthorization>
          <RecapsPageContent />
        </AllPagesAuthorization>
      </Route>
      <Route exact path={`${basePath}/all_users_and_teams`}>
        <AllUsersAuthorization>
          <RecapsPageContent />
        </AllUsersAuthorization>
      </Route>
      <Route exact path={`${basePath}/all_users`}>
        <AllUsersAuthorization>
          <RecapsPageContent />
        </AllUsersAuthorization>
      </Route>
      <Route exact path={`${basePath}/team/:team_id`}>
        <PageAuthorization>
          <RecapsPageContent />
        </PageAuthorization>
      </Route>
      <Route exact path={`${basePath}/user_group/:user_group_id`}>
        <UserGroupAuthorization>
          <RecapsPageContent />
        </UserGroupAuthorization>
      </Route>
      <Route exact path={`${basePath}/user/:user_id`}>
        <UserAuthorization>
          <RecapsPageContent />
        </UserAuthorization>
      </Route>
      <Route component={RecapsPageContent} />
    </Switch>;
};

require("./recaps_page.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./recaps_page.js");